import { createContext, useContext, useState, useMemo, useCallback, ReactNode } from 'react';
import { useLocation, useParams } from 'react-router-dom';

interface PageContext {
  page: string;
  projectId?: string;
  leadId?: string;
  tab?: string;
  [key: string]: unknown;
}

interface ChatContextType {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  conversationId: string | null;
  setConversationId: (id: string | null) => void;
  showHistory: boolean;
  setShowHistory: (show: boolean) => void;
  pageContext: PageContext;
  setPageContextOverride: (ctx: Partial<PageContext>) => void;
}

const ChatContext = createContext<ChatContextType | null>(null);

export function useChatContext() {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChatContext must be used within ChatProvider');
  return ctx;
}

export function ChatProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [override, setOverride] = useState<Partial<PageContext>>({});

  const location = useLocation();
  const params = useParams();

  const setPageContextOverride = useCallback((ctx: Partial<PageContext>) => {
    setOverride(ctx);
  }, []);

  const pageContext = useMemo<PageContext>(() => {
    const ctx: PageContext = { page: location.pathname, ...override };

    if (params.id) {
      if (location.pathname.includes('/project/')) {
        ctx.projectId = params.id;
      } else if (location.pathname.includes('/lead/')) {
        ctx.leadId = params.id;
      }
    }

    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    if (tab) ctx.tab = tab;

    return ctx;
  }, [location.pathname, location.search, params.id, override]);

  const value = useMemo(() => ({
    isOpen,
    setIsOpen,
    conversationId,
    setConversationId,
    showHistory,
    setShowHistory,
    pageContext,
    setPageContextOverride,
  }), [isOpen, conversationId, showHistory, pageContext, setPageContextOverride]);

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
}
